// src/components/admin/AdminPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import SideNav from "../SideNav";

const PAGE_BG =
  "radial-gradient(circle at 20% 10%, #92bad4, rgba(255,255,255,0.00) 45%), " +
  "radial-gradient(circle at 90% 80%, #92bad4, rgba(244,124,0,0.00) 50%), #ffffff";

const TABLES = ["ProjectAccess", "Projects", "ProjectState"];

// Kolomdefinities per tabel
const COLUMNS = {
  ProjectAccess: ["TenantId", "ProjectId", "UserEmail", "Role"],
  Projects:      ["TenantId", "ProjectId", "ProjectName", "LogoPath", "Domains", "DashboardType"],
  ProjectState:  ["TenantId", "ProjectId", "SchemaVersion", "ContentVersion", "StateJson", "UpdatedAt", "UpdatedBy"],
};

// Primaire sleutels per tabel
const PK_FIELDS = {
  ProjectAccess: ["TenantId", "ProjectId", "UserEmail"],
  Projects:      ["TenantId", "ProjectId"],
  ProjectState:  ["TenantId", "ProjectId"],
};

// Velden die bewerkbaar zijn (niet de PK)
const EDITABLE_FIELDS = {
  ProjectAccess: ["Role"],
  Projects:      ["ProjectName", "LogoPath", "Domains", "DashboardType"],
  ProjectState:  ["StateJson", "SchemaVersion", "ContentVersion"],
};

// Velden met grote content die een textarea nodig hebben
const TEXTAREA_FIELDS = new Set(["StateJson"]);

// Placeholder-tekst per veld
const PLACEHOLDERS = {
  Domains: "bijv. Sociaal domein;Ruimtelijk domein;Bedrijfsvoering",
};

// Helptext onder het veld
const FIELD_HINTS = {
  Domains: "Scheid meerdere domeinen met een puntkomma (;).",
};

// Velden die een <select> dropdown renderen, met hun opties
const SELECT_FIELDS = {
  DashboardType: [
    { value: "ea", label: "EA Dashboard" },
    { value: "pl", label: "Projectleiders Dashboard" },
    { value: "vw", label: "Volwassenheid Dashboard" },
  ],
};

// Standaardwaarden voor nieuwe rijen (overschrijft lege string)
const FIELD_DEFAULTS = {
  DashboardType: "ea",
};

function cellValue(col, value) {
  if (value === null || value === undefined) return "—";
  if (col === "StateJson") {
    const str = String(value);
    return str.length > 120 ? str.slice(0, 120) + "…" : str;
  }
  if (col === "UpdatedAt") {
    return value ? new Date(value).toLocaleString("nl-NL") : "—";
  }
  return String(value);
}

function emptyForm(table) {
  return Object.fromEntries(
    COLUMNS[table].map((c) => [c, FIELD_DEFAULTS[c] ?? ""])
  );
}

// ── Formulier-paneel ───────────────────────────────────────────────────────────
function FormPanel({ table, mode, initialData, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState(initialData || emptyForm(table));

  useEffect(() => {
    setForm(initialData || emptyForm(table));
  }, [initialData, table]);

  const allFields = mode === "add" ? COLUMNS[table] : EDITABLE_FIELDS[table];
  const pkFields = PK_FIELDS[table];

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSave(form);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl p-4 mb-4"
      style={{
        background: "linear-gradient(145deg, #eff6ff, #dbeafe)",
        border: "1px solid rgba(59,130,246,0.25)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
      }}
    >
      <div className="text-sm font-bold mb-3" style={{ color: "#27435d" }}>
        {mode === "add" ? "Nieuwe rij toevoegen" : "Rij bewerken"}
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        {allFields.map((field) => {
          const isPk = pkFields.includes(field) && mode === "edit";
          const isTextarea = TEXTAREA_FIELDS.has(field);
          const selectOptions = SELECT_FIELDS[field];

          return (
            <div key={field} className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#475569" }}>
                {field === "DashboardType" ? "Dashboard type" : field}{isPk ? " (sleutel)" : ""}
              </label>
              {selectOptions ? (
                <select
                  value={form[field] ?? FIELD_DEFAULTS[field] ?? ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  disabled={isPk}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 13,
                    background: isPk ? "#f1f5f9" : "#ffffff",
                    color: "#0f172a",
                  }}
                >
                  {selectOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : isTextarea ? (
                <textarea
                  value={form[field] ?? ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  rows={5}
                  disabled={isPk}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 12,
                    fontFamily: "monospace",
                    background: isPk ? "#f1f5f9" : "#ffffff",
                    color: "#0f172a",
                    resize: "vertical",
                    gridColumn: "1 / -1",
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={form[field] ?? ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  disabled={isPk}
                  placeholder={PLACEHOLDERS[field] ?? ""}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 13,
                    background: isPk ? "#f1f5f9" : "#ffffff",
                    color: "#0f172a",
                  }}
                />
              )}
              {FIELD_HINTS[field] && (
                <span className="text-xs text-slate-500">{FIELD_HINTS[field]}</span>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <p className="mt-3 text-xs font-semibold" style={{ color: "#dc2626" }}>
          {error}
        </p>
      )}

      <div className="flex gap-2 mt-4">
        <button
          type="submit"
          disabled={saving}
          className="login-btn"
          style={{ width: "auto", marginTop: 0, marginBottom: 0, minWidth: 120, padding: "8px 18px" }}
        >
          {saving ? "Opslaan…" : "Opslaan"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "8px 18px",
            borderRadius: 10,
            border: "none",
            background: "linear-gradient(145deg, #64748b, #475569)",
            color: "#ffffff",
            fontWeight: 700,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          Annuleren
        </button>
      </div>
    </form>
  );
}

// ── Tabelweergave ──────────────────────────────────────────────────────────────
function TableView({ table, rows, onEdit, onDeleteRequest, deleteConfirmPk }) {
  const cols = COLUMNS[table];
  const pkFields = PK_FIELDS[table];

  function pkOf(row) {
    return Object.fromEntries(pkFields.map((f) => [f, row[f]]));
  }

  function pkKey(pk) {
    return pkFields.map((f) => pk[f]).join("|");
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#27435d" }}>
            {cols.map((col) => (
              <th
                key={col}
                style={{
                  padding: "10px 12px",
                  textAlign: "left",
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  whiteSpace: "nowrap",
                }}
              >
                {col}
              </th>
            ))}
            <th style={{ padding: "10px 12px", color: "#ffffff", fontWeight: 700, fontSize: 11, textTransform: "uppercase", whiteSpace: "nowrap", minWidth: 160 }}>
              Acties
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={cols.length + 1} style={{ padding: "20px 12px", textAlign: "center", color: "#94a3b8" }}>
                Geen rijen gevonden.
              </td>
            </tr>
          )}
          {rows.map((row, i) => {
            const pk = pkOf(row);
            const key = pkKey(pk);
            const isConfirming = deleteConfirmPk && pkKey(deleteConfirmPk) === key;

            return (
              <tr
                key={key}
                style={{
                  background: i % 2 === 0 ? "#f8fafc" : "#ffffff",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                {cols.map((col) => (
                  <td
                    key={col}
                    style={{
                      padding: "9px 12px",
                      color: "#1e293b",
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: col === "StateJson" ? "nowrap" : "normal",
                    }}
                    title={col === "StateJson" ? String(row[col] ?? "") : undefined}
                  >
                    {cellValue(col, row[col])}
                  </td>
                ))}
                <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                  {isConfirming ? (
                    <span className="flex items-center gap-2" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Zeker verwijderen?</span>
                      <button
                        onClick={() => onDeleteRequest(pk, true)}
                        style={{
                          padding: "4px 10px", borderRadius: 7, border: "none",
                          background: "#dc2626", color: "#fff", fontWeight: 700,
                          fontSize: 12, cursor: "pointer",
                        }}
                      >
                        Ja
                      </button>
                      <button
                        onClick={() => onDeleteRequest(null, false)}
                        style={{
                          padding: "4px 10px", borderRadius: 7, border: "none",
                          background: "#64748b", color: "#fff", fontWeight: 700,
                          fontSize: 12, cursor: "pointer",
                        }}
                      >
                        Nee
                      </button>
                    </span>
                  ) : (
                    <span style={{ display: "flex", gap: 6 }}>
                      <button
                        onClick={() => onEdit(row)}
                        style={{
                          padding: "4px 12px", borderRadius: 7, border: "none",
                          background: "#31719D", color: "#fff", fontWeight: 700,
                          fontSize: 12, cursor: "pointer",
                        }}
                      >
                        Bewerken
                      </button>
                      <button
                        onClick={() => onDeleteRequest(pk, false)}
                        style={{
                          padding: "4px 12px", borderRadius: 7, border: "none",
                          background: "#dc2626", color: "#fff", fontWeight: 700,
                          fontSize: 12, cursor: "pointer",
                        }}
                      >
                        Verwijderen
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab-sectie ─────────────────────────────────────────────────────────────────
function TableSection({ table }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [mode, setMode] = useState(null);       // "add" | "edit" | null
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteConfirmPk, setDeleteConfirmPk] = useState(null);

  const pkFields = PK_FIELDS[table];

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`/api/beheer?table=${table}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [table]);

  useEffect(() => { load(); }, [load]);

  function handleEdit(row) {
    setEditRow(row);
    setMode("edit");
    setFormError(null);
    setDeleteConfirmPk(null);
  }

  function handleAdd() {
    setEditRow(null);
    setMode("add");
    setFormError(null);
    setDeleteConfirmPk(null);
  }

  function handleCancel() {
    setMode(null);
    setEditRow(null);
    setFormError(null);
  }

  async function handleSave(form) {
    setSaving(true);
    setFormError(null);
    try {
      let res;
      if (mode === "add") {
        res = await fetch(`/api/beheer?table=${table}`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(form),
        });
      } else {
        const pk = Object.fromEntries(pkFields.map((f) => [f, editRow[f]]));
        const data = Object.fromEntries(
          EDITABLE_FIELDS[table].map((f) => [f, form[f]])
        );
        res = await fetch(`/api/beheer?table=${table}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ pk, data }),
        });
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      setMode(null);
      setEditRow(null);
      await load();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteRequest(pk, confirmed) {
    if (!confirmed) {
      // Toon bevestiging of annuleer
      setDeleteConfirmPk(pk);
      return;
    }
    // Verwijderen bevestigd
    try {
      const res = await fetch(`/api/beheer?table=${table}`, {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pk }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Verwijderen mislukt: ${err.error || res.status}`);
        return;
      }
      setDeleteConfirmPk(null);
      await load();
    } catch (e) {
      alert(`Verwijderen mislukt: ${e.message}`);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#ffffff" }}>
          {rows.length} rij{rows.length !== 1 ? "en" : ""}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            style={{
              padding: "6px 14px", borderRadius: 9, border: "none",
              background: "rgba(255,255,255,0.15)", color: "#ffffff",
              fontWeight: 700, fontSize: 12, cursor: "pointer",
            }}
          >
            Vernieuwen
          </button>
          <button
            type="button"
            onClick={handleAdd}
            className="login-btn"
            style={{ width: "auto", marginTop: 0, marginBottom: 0, padding: "6px 16px" }}
          >
            + Toevoegen
          </button>
        </div>
      </div>

      {mode && (
        <FormPanel
          table={table}
          mode={mode}
          initialData={editRow}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={formError}
        />
      )}

      <div className="card-3d" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="portal-loading" style={{ padding: "24px 0" }}>
            <div className="portal-spinner" />
            <p className="portal-loading-text">Laden…</p>
          </div>
        ) : loadError ? (
          <p style={{ padding: 16, color: "#dc2626", fontSize: 13 }}>
            Fout bij laden: {loadError}
          </p>
        ) : (
          <TableView
            table={table}
            rows={rows}
            onEdit={handleEdit}
            onDeleteRequest={handleDeleteRequest}
            deleteConfirmPk={deleteConfirmPk}
          />
        )}
      </div>
    </div>
  );
}

// ── Hoofdcomponent ─────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [activeTab, setActiveTab] = useState("ProjectAccess");

  return (
    <div className="min-h-screen" style={{ background: PAGE_BG }}>
      <SideNav />
      <div className="header-3d">
        <div className="max-w-6xl mx-auto px-4 py-4" style={{ background: "#31719D" }}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-white">Beheerportaal</h1>
              <p className="mt-1 text-sm" style={{ color: "#d6e8f3" }}>
                Tabelbeheer voor ProjectAccess, Projects en ProjectState. Alleen toegankelijk voor admin_app.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5">
        <div className="card-3d rounded-2xl overflow-hidden" style={{ background: "#31719D" }}>
          {/* Tabs */}
          <div className="flex flex-wrap" style={{ background: "#31719D", padding: "10px 14px", gap: "8px" }}>
            {TABLES.map((t) => (
              <button
                key={t}
                type="button"
                className={"tab-3d text-sm " + (activeTab === t ? "active" : "")}
                onClick={() => setActiveTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="px-4 py-5" style={{ background: "#31719D" }}>
            <TableSection key={activeTab} table={activeTab} />
          </div>
        </div>

        <p
          className="text-[11px] text-center mt-4"
          style={{ color: "#000000" }}
        >
          © Datavolwassenheid.nl® B.V. — Beheerportaal, alleen voor geautoriseerde beheerders.
        </p>
      </div>
    </div>
  );
}
